import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { copy } from 'src/components/common/CopyOnClick/CopyOnClick';
import Portal from 'src/components/common/Portal';
import { getBaseUrl } from 'src/functions/functions';
import useResponsive from 'src/hooks/contexts/useResponsive';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGHeading from 'src/styles/theme/components/NGHeading';
import NGIcon from 'src/styles/theme/components/NGIcon';
import { EIcon } from 'src/styles/theme/components/NGIcon/NGIcon.enum';
import { EFontFamily, ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import NGTooltip from 'src/styles/theme/components/NGTooltip';
import { ESize } from 'src/styles/theme/utils/enums';
import { ESocialShareType, ESocialType } from './ShareProfile.enum';
import { NeutralButtonEx, StyledShareProfile } from './ShareProfile.styles';
import { IShareProfile } from './ShareProfile.type';

const BASE_URL = getBaseUrl();
const BASE_LINK = `${BASE_URL}/character/{var1}`;

const BASE_TEXT = `Join%20me%20and%20the%20Node%20Guardians%20on%20epic%20quests%20to%20improve%20your%20Web3%20skills%20%F0%9F%9B%A1%0A%0A`;

const QUEST_BASE_TEXT =
	'I%E2%80%99ve+just+completed+the+{var1}+quest+on+Node+Guardians%2C+come+and+check+it+out%21%20%F0%9F%9B%A1%0A%0A';

const BASE_QUEST_LINK = `${BASE_URL}/dev-hub/quests/{var2}`;

const baseTwitter = {
	label: 'Twitter',
	icon: EIcon.twitter,
	type: ESocialType.twitter
};

const baseTelegram = {
	label: 'Telegram',
	icon: EIcon.telegram,
	type: ESocialType.telegram
};

const baseCopy = {
	label: 'Copy Link',
	icon: EIcon.link,
	type: ESocialType.link
};

const profileSocials = [
	{
		link: `https://twitter.com/intent/tweet?text=${BASE_TEXT}${BASE_LINK}`,
		...baseTwitter
	},
	{
		link: `https://t.me/share/url?url=${BASE_LINK}&text=${BASE_TEXT}`,
		...baseTelegram
	},
	{
		link: BASE_LINK,
		...baseCopy
	}
];

const questSocials = [
	{
		link: `https://twitter.com/intent/tweet?text=${QUEST_BASE_TEXT}${BASE_QUEST_LINK}`,
		...baseTwitter
	},
	{
		link: `https://t.me/share/url?url=${BASE_QUEST_LINK}&text=${QUEST_BASE_TEXT}`,
		...baseTelegram
	},
	{
		link: BASE_QUEST_LINK,
		...baseCopy
	}
];

const appSocials = [
	{
		link: `https://twitter.com/intent/tweet?text=${BASE_TEXT}${BASE_URL}`,
		...baseTwitter
	},
	{
		link: `https://t.me/share/url?url=${BASE_URL}&text=${BASE_TEXT}`,
		...baseTelegram
	},
	{
		link: BASE_URL,
		...baseCopy
	}
];

const callShareApi = async (variables: string[], shareType: ESocialShareType) => {
	let title = '';
	let text = '';
	let url = '';

	switch (shareType) {
		case ESocialShareType.profile:
			title = 'Node Guardians';
			text = 'Join me and the Node Guardians on epic quests to improve your Web3 skills 🛡️';
			url = BASE_LINK.replace('{var1}', variables[0]);
			break;
		case ESocialShareType.quest:
			title = 'Node Guardians';
			text = `I've just completed the "${variables[0]}" quest on Node Guardians, come and check it out!`;
			url = BASE_QUEST_LINK.replace('{var1}', variables[0]).replace('{var2}', variables[1]);
			break;
		case ESocialShareType.app:
			title = 'Node Guardians';
			text = 'Join me and the Node Guardians on epic quests to improve your Web3 skills 🛡️';
			url = BASE_URL;
		default:
			return false;
	}

	if (navigator?.share) {
		await navigator
			.share({
				title: title,
				text: text,
				url: url
			})
			.then(() => {
				return true;
			})
			.catch((err) => {
				console.log(`Couldn't share because of`, err.message);
				return false;
			});
	} else {
		// fallback
		console.log('Share API not supported');
		return false;
	}
};

const ShareProfile: FC<IShareProfile> = ({
	isHidden = false,
	shareType,
	shareVariables = [''],
	isMobileQuest = false,
	...otherProps
}) => {
	const { screenFrames, isSmallerThanSm } = useResponsive();

	const [isCopying, setIsCopying] = useState(false);
	const [isVisible, setIsVisible] = useState(false);

	const isShareApiSupported = isSmallerThanSm && typeof navigator !== 'undefined' && navigator?.share !== undefined;

	const handleCopyValue = useCallback(
		(value: string) => {
			let formattedValue = value.replaceAll('{var1}', shareVariables[0]);

			if (shareVariables[1]) {
				formattedValue = formattedValue.replaceAll('{var2}', shareVariables[1]);
			}

			copy(formattedValue);
			setIsCopying(true);

			setTimeout(() => {
				setIsCopying(false);
			}, 1500);
		},
		[shareVariables]
	);

	const handleShareApi = useCallback(async () => {
		const res = await callShareApi(shareVariables, shareType);

		if (!res) {
			switch (shareType) {
				case ESocialShareType.profile:
					handleCopyValue(BASE_LINK);
					break;
				case ESocialShareType.quest:
					handleCopyValue(BASE_QUEST_LINK);
				case ESocialShareType.app:
					handleCopyValue(BASE_URL);
					break;
				default:
					break;
			}
		}
	}, [handleCopyValue, shareType, shareVariables]);

	const CopyButton = (icon: EIcon, onClick: () => void) => {
		return (
			<NeutralButtonEx onClick={onClick} isActive={isCopying}>
				<NGIcon type={icon} size={ESize.l} />
				<NGIcon type={EIcon.check} size={ESize.l} />
			</NeutralButtonEx>
		);
	};

	const selectedSocials = useMemo(() => {
		switch (shareType) {
			case ESocialShareType.profile:
				return profileSocials;
			case ESocialShareType.quest:
				return questSocials;
			case ESocialShareType.app:
				return appSocials;
			default:
				return [];
		}
	}, [shareType]);

	useEffect(() => {
		if (!isHidden) {
			setTimeout(() => {
				setIsVisible(true);
			}, 10);
		} else {
			setIsVisible(false);
		}
	}, [isHidden]);

	return (
		<Portal disabled={shareType === ESocialShareType.quest || isMobileQuest}>
			<StyledShareProfile
				isShareApiSupported={isShareApiSupported}
				isHidden={!isVisible}
				shareType={shareType}
				isMobileQuest={isMobileQuest}
				{...otherProps}
			>
				{shareType !== ESocialShareType.app ? (
					<NGHeading
						type={ETextType.h4}
						as='p'
						fontFamily={EFontFamily.cinzel}
						isHidden={!isMobileQuest && (screenFrames?.height || 0) < 745 && (screenFrames?.width || 0) > 1200}
					>
						{shareType === ESocialShareType.profile ? 'Share your profile' : 'Share this quest'}
					</NGHeading>
				) : null}

				<Flex
					mdDirection={!isMobileQuest || shareType === ESocialShareType.app ? EFlex.column : undefined}
					horizontal={isShareApiSupported ? EFlex.end : EFlex.start}
				>
					{isShareApiSupported
						? CopyButton(EIcon.share, () => handleShareApi())
						: selectedSocials.map(({ label, type, icon, link }) => (
								<Fragment key={type}>
									{type === ESocialType.link ? (
										<NGTooltip content='Click to copy'>{CopyButton(icon, () => handleCopyValue(link))}</NGTooltip>
									) : (
										<a
											data-cy={`share-${label}`}
											href={link
												.replaceAll('{var1}', shareVariables?.[0] || '')
												.replaceAll('{var2}', shareVariables?.[1] || '')}
											target='_blank'
											rel='noreferrer'
										>
											<NGIcon type={icon} size={ESize.l} />
										</a>
									)}
								</Fragment>
						  ))}
				</Flex>
			</StyledShareProfile>
		</Portal>
	);
};

export { ShareProfile };
